<template>
  <v-card class="timeline-profile" v-if="agency">
    <!-- Header -->
    <div class="profile-header">
      <h1 class="agency-name">{{ agency.name }}</h1>
      <icon-button
        v-if="profile._id == agency._id"
        :onClicked="onEditTitle"
        icon="mdi-pencil"
        text="Edit Profile"
        class="edit-profile-btn"
      />
    </div>

    <!-- Timeline -->
    <div class="timeline">
      <!-- Causes -->
      <div class="timeline-item">
        <div class="timeline-badge cause">
          <v-icon color="#3B7CA1">mdi mdi-account-heart</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>Causes</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onEditCause"
              icon="mdi-pencil"
              text="Edit"
              class="edit-btn"
            />
          </div>
          <div class="icon-grid">
            <site-icon
              v-for="(icon, key) in agency.causes"
              :key="'cause-' + key"
              :name="icon"
              type="Cause"
              small
            />
          </div>
        </div>
      </div>

      <!-- Who We Are -->
      <div class="timeline-item">
        <div class="timeline-badge about">
          <v-icon color="#3B7CA1">mdi mdi-account-group</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>Who We Are</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onEditWhoWeAre"
              icon="mdi-pencil"
              text="Edit"
              class="edit-btn"
            />
          </div>
          <p class="who-para">{{ agency.who_we_are }}</p>
        </div>
      </div>

      <!-- What We Do -->
      <div class="timeline-item">
        <div class="timeline-badge activity">
          <v-icon color="#3B7CA1">mdi mdi-information-outline</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>What We Do</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onEditWhatWeDo"
              icon="mdi-pencil"
              text="Edit"
              class="edit-btn"
            />
          </div>
          <p class="what-para">{{ agency.what_we_do }}</p>
        </div>
      </div>

      <!-- Location -->
      <div class="timeline-item">
        <div class="timeline-badge location">
          <v-icon color="#3B7CA1">mdi-map-marker</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>Location</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onEditLocation"
              icon="mdi-pencil"
              text="Edit"
              class="edit-btn"
            />
          </div>
          <div class="info-item" v-if="agency.address">
            <!-- <v-icon color="#3B7CA1">mdi-map-marker</v-icon> -->
            <span>
              {{
                `${agency.address}, 
              ${agency.city}, ${agency.state} ${agency.zip}`
              }}
            </span>
          </div>
           <div class="info-item" v-if="agency.address2">
            <!-- <v-icon color="#3B7CA1">mdi-map-marker</v-icon> -->
            <span>
              {{
                `${
                  agency.address2 ? " " + agency.address2 : ""
                }, 
              ${agency.city}, ${agency.state} ${agency.zip}`
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Contacts -->
      <div class="timeline-item">
        <div class="timeline-badge contact">
          <v-icon color="#3B7CA1">mdi mdi-contacts-outline</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>Contacts</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onEditContacts"
              icon="mdi-pencil"
              text="Edit"
              class="edit-btn"
            />
          </div>
          <div class="info-list">
            <div class="info-item" v-if="agency.phone">
              <v-icon color="#3B7CA1">mdi-phone</v-icon>
              <span>{{ formatPhoneNumber(agency.phone) }}</span>
            </div>
              <div class="info-item" v-if="agency.email">
              <v-icon color="#3B7CA1">mdi mdi-email-outline</v-icon>
              <span>{{ agency.email }}</span>
            </div>
             <div class="info-item" v-if="agency.contact_person">
              <v-icon color="#3B7CA1">mdi mdi-account</v-icon>
              <span>{{ agency.contact_person }}</span>
            </div>
             <div class="info-item" v-if="agency.contact_title">
              <v-icon color="#3B7CA1">mdi mdi-account-box-outline</v-icon>
              <span>{{ agency.contact_title }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Links -->
      <div class="timeline-item">
        <div class="timeline-badge link">
          <v-icon color="#3B7CA1">mdi-link-variant</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>Links</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onEditLinks"
              icon="mdi-pencil"
              text="Edit"
              class="edit-btn"
            />
          </div>

          <div class="links-list">
            <a
              v-if="agency.website"
              :href="getSafeUrl(agency.website)"
              target="_blank"
              class="link-item website"
            >
              <v-icon color="#2c3e50">mdi-web</v-icon>
              <span>{{ getHostname(agency.website) }}</span>
              <v-icon small class="external-icon">mdi-open-in-new</v-icon>
            </a>

            <a
              v-if="agency.facebook"
              :href="getSafeSocialUrl(agency.facebook, 'facebook')"
              target="_blank"
              class="link-item facebook"
            >
              <v-icon color="#1877f2">mdi-facebook</v-icon>
              <span>{{ getSocialDisplayName(agency.facebook, 'facebook') }}</span>
              <v-icon small class="external-icon">mdi-open-in-new</v-icon>
            </a>

            <a
              v-if="agency.twitter"
              :href="getSafeSocialUrl(agency.twitter, 'twitter')"
              target="_blank"
              class="link-item twitter"
            >
              <v-icon color="#1da1f2">mdi-twitter</v-icon>
              <span>{{ getSocialDisplayName(agency.twitter, 'twitter') }}</span>
              <v-icon small class="external-icon">mdi-open-in-new</v-icon>
            </a>
          </div>
        </div>
      </div>

      <!-- Photos -->
      <div
        class="timeline-item"
        v-if="agency.photos.length != 0 || agency._id == profile._id"
      >
        <div class="timeline-badge photo">
          <v-icon color="#3B7CA1">mdi mdi-image-multiple</v-icon>
        </div>
        <div class="timeline-card">
          <div class="card-header">
            <h2>Photos</h2>
            <icon-button
              v-if="profile._id == agency._id"
              :onClicked="onAddPhoto"
              icon="mdi-plus"
              text="Add Photo"
              class="edit-btn"
            />
          </div>
          <agency-photo-viewer
            :agency_id="agency._id"
            :editable="agency._id == profile._id"
            ref="viewer"
          />
        </div>
      </div>
    </div>
    <agency-profile-edit-title
      :dialog="editTitleDialog"
      :onClose="onCloseEditDialog"
      v-if="editTitleDialog"
    />
    <agency-profile-edit-causes-dialog
      :dialog="editCauseDialog"
      :onClose="onCloseEditDialog"
      v-if="editCauseDialog"
    />
    <agency-profile-edit-who-we-are
      :dialog="editWhoWeAreDialog"
      :onClose="onCloseEditDialog"
      v-if="editWhoWeAreDialog"
    />
    <agency-profile-edit-what-we-do
      :dialog="editWhatWeDoDialog"
      :onClose="onCloseEditDialog"
      v-if="editWhatWeDoDialog"
    />
    <agency-profile-edit-location
      :dialog="editLocationDialog"
      :onClose="onCloseEditDialog"
      v-if="editLocationDialog"
    />
    <agency-profile-edit-contacts
      :dialog="editContactsDialog"
      :onClose="onCloseEditDialog"
      v-if="editContactsDialog"
    />
    <agency-profile-edit-links
      :dialog="editLinksDialog"
      :onClose="onCloseEditDialog"
      v-if="editLinksDialog"
    />
  </v-card>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import AgencyPhotoViewer from "@/components/agency/AgencyPhotoViewer.vue";
import IconButton from "@/components/common/IconButton.vue";
import AgencyProfileEditTitle from "./edit/AgencyProfileEditTitle.vue";
import AgencyProfileEditCausesDialog from "./edit/AgencyProfileEditCausesDialog.vue";
import AgencyProfileEditWhoWeAre from "./edit/AgencyProfileEditWhoWeAre.vue";
import AgencyProfileEditWhatWeDo from "./edit/AgencyProfileEditWhatWeDo.vue";
import AgencyProfileEditLocation from "./edit/AgencyProfileEditLocation.vue";
import AgencyProfileEditContacts from "./edit/AgencyProfileEditContacts.vue";
import AgencyProfileEditLinks from "./edit/AgencyProfileEditLinks.vue";
import SiteIcon from "@/components/common/SiteIcon.vue";
export default {
  components: {
    AgencyPhotoViewer,
    IconButton,
    AgencyProfileEditTitle,
    AgencyProfileEditCausesDialog,
    AgencyProfileEditWhoWeAre,
    AgencyProfileEditWhatWeDo,
    AgencyProfileEditLocation,
    AgencyProfileEditContacts,
    AgencyProfileEditLinks,
    SiteIcon
  },
  data() {
    return {
      loading: false,
      editTitleDialog: false,
      editCauseDialog: false,
      editWhoWeAreDialog: false,
      editWhatWeDoDialog: false,
      editLocationDialog: false,
      editContactsDialog: false,
      editLinksDialog: false
    };
  },
  computed: {
    ...mapGetters({
      profile: "auth/getProfile",
      profileType: "auth/getType",
      agency: "agency/getAgency"
    })
  },
  methods: {
    ...mapActions("agency", {
      copyAgencyFromProfile: "copyFromProfile"
    }),
    onEditTitle() {
      this.editTitleDialog = true;
    },
    onEditCause() {
      this.editCauseDialog = true;
    },
    onEditWhoWeAre() {
      this.editWhoWeAreDialog = true;
    },
    onEditWhatWeDo() {
      this.editWhatWeDoDialog = true;
    },
    onEditLocation() {
      this.editLocationDialog = true;
    },
    onEditContacts() {
      this.editContactsDialog = true;
    },
    onEditLinks() {
      this.editLinksDialog = true;
    },
  
  // ✅ Always return a safe clickable URL
  getSafeUrl(url) {
    if (!url) return null;

    // Trim spaces
    url = url.trim();

    // Add https:// if missing
    if (!/^https?:\/\//i.test(url)) {
      // If it starts with 'www.' or 'facebook.com', 'twitter.com', 'linkedin.com' etc.
      if (
        url.startsWith("www.") ||
        url.startsWith("facebook.com") ||
        url.startsWith("twitter.com") ||
        url.startsWith("linkedin.com")
      ) {
        return `https://${url}`;
      }

      // Otherwise, also prepend https://
      return `https://${url}`;
    }

    return url;
  },

  // ✅ Get safe URL for social media platforms
  getSafeSocialUrl(url, platform) {
    if (!url) return null;

    // Trim spaces
    url = url.trim();

    // Platform base URLs
    const platformBases = {
      facebook: 'https://facebook.com/',
      twitter: 'https://twitter.com/',
      instagram: 'https://instagram.com/',
      linkedin: 'https://linkedin.com/in/',
      youtube: 'https://youtube.com/'
    };

    // If it already has a protocol, return as is
    if (/^https?:\/\//i.test(url)) {
      return url;
    }

    // If it starts with the platform domain, add protocol
    if (url.startsWith(`${platform}.com`)) {
      return `https://${url}`;
    }

    // If it's just a username/handle for social platforms
    if (platform && platformBases[platform]) {
      // Remove any leading @ or / characters
      const cleanUsername = url.replace(/^[@/]+/, '');
      
      // If it doesn't contain any domain, treat it as a username
      if (!cleanUsername.includes('.com') && !cleanUsername.includes('www.')) {
        return platformBases[platform] + cleanUsername;
      }
    }

    // Fallback to generic URL formatting
    return this.getSafeUrl(url);
  },

  // ✅ Extract hostname cleanly
  getHostname(url) {
    try {
      const safeUrl = this.getSafeUrl(url);
      return new URL(safeUrl).hostname;
    } catch (e) {
      return url;
    }
  },

  // ✅ Get display name for social media links
  getSocialDisplayName(url, platform) {
    if (!url) return '';
    
    // Platform hostnames for display
    const platformHosts = {
      facebook: 'facebook.com',
      twitter: 'twitter.com', 
      instagram: 'instagram.com',
      linkedin: 'linkedin.com',
      youtube: 'youtube.com'
    };
    
    try {
      // Use the safe social URL to get proper hostname
      const safeUrl = this.getSafeSocialUrl(url, platform);
      const hostname = new URL(safeUrl).hostname;
      
      // If it's a recognized social platform hostname, return it
      if (Object.values(platformHosts).includes(hostname)) {
        return hostname;
      }
      
      // If the platform is specified, return the expected platform hostname
      if (platform && platformHosts[platform]) {
        return platformHosts[platform];
      }
      
      // Fallback to regular hostname extraction
      return hostname;
    } catch (e) {
      // If everything fails, return the expected platform hostname
      return platform && platformHosts[platform] ? platformHosts[platform] : url;
    }
  },
 
    onCloseEditDialog() {
      this.editTitleDialog = false;
      this.editCauseDialog = false;
      this.editWhoWeAreDialog = false;
      this.editWhatWeDoDialog = false;
      this.editContactsDialog = false;
      this.editLinksDialog = false;
      this.editLocationDialog = false;
      this.copyAgencyFromProfile();
    },
    onAddPhoto() {
      this.$refs.viewer.onAddPhoto();
    },
    formatPhoneNumber(str) {
      let cleaned = ("" + str).replace(/\D/g, "");
      let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        let intlCode = match[1] ? "+1 " : "";
        return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join(
          ""
        );
      }
      return str;
    }
  }
};
</script>

<style scoped>
.links-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.link-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-radius: 8px;
  text-decoration: none;
  color: #2c3e50;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
}

.link-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.link-item.website:hover {
  background-color: rgba(44, 62, 80, 0.05);
  /* border-color: #2c3e50; */
}

.link-item.facebook:hover {
  background-color: rgba(24, 119, 242, 0.05);
  border-color: #1877f2;
}

.link-item.twitter:hover {
  background-color: rgba(29, 161, 242, 0.05);
  border-color: #1da1f2;
}

.link-item .v-icon {
  margin-right: 12px;
  font-size: 20px;
}

.link-item span {
  flex-grow: 1;
  font-weight: 500;
}

.external-icon {
  color: #7f8c8d;
  margin-left: 8px;
  opacity: 0.7;
}

@media (max-width: 600px) {
  .link-item {
    padding: 8px 12px;
  }

  .link-item .v-icon {
    margin-right: 8px;
    font-size: 18px;
  }
}
.timeline-profile {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.profile-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  padding: 32px;
  color: white;
  text-align: center;
  position: relative;
}

.agency-name {
  font-size: 28px;
  font-weight: 600;
  margin: 0;
}

.edit-profile-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  color: white;
}

.timeline {
  position: relative;
  padding: 40px 0;
  max-width: 900px;
  margin: 0 auto;
}

.timeline::before {
  content: "";
  position: absolute;
  top: 0;
  left: 40px;
  height: 100%;
  width: 3px;
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.timeline-item {
  position: relative;
  padding-left: 80px;
  margin-bottom: 40px;
}

.timeline-badge {
  position: absolute;
  left: 28px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: white;
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.timeline-badge::after {
  content: "";
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

/* .timeline-badge.cause::after { background: #472583; } */
/* .timeline-badge.about::after { background: #0a5b8a; } */
/* .timeline-badge.activity::after { background: #fdbc3a; } */
/* .timeline-badge.location::after { background: #4caf50; } */
/* .timeline-badge.contact::after { background: #e91e63; } */
/* .timeline-badge.link::after { background: #9c27b0; } */
/* .timeline-badge.photo::after { background: #ff9800; } */

.timeline-card {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.timeline-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-header h2 {
  font-size: 20px;
  color: #0a5b8a;
  margin: 0;
}

.edit-btn {
  color: #666;
}

.icon-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}

.links-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.link-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0a5b8a;
  text-decoration: none;
}

.link-item:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .timeline::before {
    left: 20px;
  }

  .timeline-item {
    padding-left: 60px;
  }

  .timeline-badge {
    left: 8px;
  }

  .profile-header {
    padding: 24px;
  }

  .agency-name {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .edit-btn {
    align-self: flex-end;
  }

  .timeline-card {
    padding: 16px;
  }
}
.who-para {
  text-align: justify;
}
.what-para {
  text-align: justify;
}
</style>
